import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { AlertData } from '@/models/alert-data.model'

interface Notification extends AlertData {
  id: string
  timestamp: number
}

export const useNotificationStore = defineStore('notification', () => {
  // State
  const currentNotification = ref<Notification | null>(null)
  const notificationHistory = ref<Notification[]>([])
  const maxHistorySize = ref(50)

  // Computed
  const hasNotification = computed(() => currentNotification.value !== null)
  const notificationCount = computed(() => notificationHistory.value.length)

  // Actions
  function showNotification(alertData: AlertData): void {
    const notification: Notification = {
      ...alertData,
      id: generateId(),
      timestamp: Date.now(),
    }

    currentNotification.value = notification

    // Add to history (excluding transient notifications)
    if (alertData.isShown) {
      addToHistory(notification)
    }
  }

  function showTopAlertMessage(isShown: boolean, type: AlertData['type'], message: string): void {
    showNotification({
      isShown,
      type,
      message,
    })
  }

  function showSuccess(message: string): void {
    showTopAlertMessage(true, 'success', message)
  }

  function showError(message: string): void {
    showTopAlertMessage(true, 'error', message)
  }

  function showWarning(message: string): void {
    showTopAlertMessage(true, 'warning', message)
  }

  function showInfo(message: string): void {
    showTopAlertMessage(true, 'info', message)
  }

  function hideNotification(): void {
    currentNotification.value = null
  }

  function clearHistory(): void {
    notificationHistory.value = []
  }

  function getNotificationById(id: string): Notification | undefined {
    return notificationHistory.value.find((n) => n.id === id)
  }

  function getRecentNotifications(count: number = 10): Notification[] {
    return notificationHistory.value.slice(-count).reverse()
  }

  // Private helpers
  function addToHistory(notification: Notification): void {
    notificationHistory.value.push(notification)

    // Trim history if it exceeds max size
    if (notificationHistory.value.length > maxHistorySize.value) {
      notificationHistory.value = notificationHistory.value.slice(-maxHistorySize.value)
    }
  }

  function generateId(): string {
    return `notification-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  return {
    // State
    currentNotification,
    notificationHistory,
    maxHistorySize,
    // Computed
    hasNotification,
    notificationCount,
    // Actions
    showNotification,
    showTopAlertMessage,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideNotification,
    clearHistory,
    getNotificationById,
    getRecentNotifications,
  }
})
